import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAffiliate, useUpdateAffiliateStatus, useUpdateAffiliate, useDeleteAffiliate } from '@/hooks/useAdmin';
import { updateAffiliateCommissionRate } from '@/services/admin.service';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ConfirmDialog, Card, CardHeader, CardBody, Badge, EmptyState, Modal } from '@/components/ui';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import type { UpdateAffiliateStatusRequest, UpdateAffiliateRequest } from '@/types/admin';
import { Tooltip } from '@/components/ui/Tooltip';
import { formatApiError } from '@/services/api';

/**
 * Admin Affiliate Detail Page
 * View and manage individual affiliate details, referrals, and payouts
 */

const STATUS_OPTIONS = ['active', 'suspended', 'inactive'] as const;

export const AdminAffiliateDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatDateTime, formatDateOnly } = useTimezone();
  const affiliateId = parseInt(id || '0', 10);

  const { data: affiliate, isLoading, error } = useAffiliate(affiliateId);
  const updateStatus = useUpdateAffiliateStatus();
  const updateAffiliate = useUpdateAffiliate();
  const deleteAffiliateMutation = useDeleteAffiliate();

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<typeof STATUS_OPTIONS[number] | null>(null);
  const [statusReason, setStatusReason] = useState('');

  // Commission rate editing states
  const [isEditingCommission, setIsEditingCommission] = useState(false);
  const [newCommissionRate, setNewCommissionRate] = useState<string>('');
  const [isUpdatingCommission, setIsUpdatingCommission] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!affiliate) return;
    try {
      await deleteAffiliateMutation.mutateAsync(affiliate.id);
      toast.success('Affiliate deleted');
      setDeleteDialog(false);
      navigate('/admin/affiliates');
    } catch (err) {
      const apiErr = formatApiError(err);
      toast.error(apiErr.message);
      setDeleteDialog(false);
    }
  };

  const handleCopyReferralCode = () => {
    if (!affiliate) return;
    navigator.clipboard.writeText(affiliate.referralCode);
    toast.success('Referral code copied to clipboard');
  };

  const handleChangeStatus = async () => {
    if (!affiliate || !selectedStatus) return;

    try {
      const data: UpdateAffiliateStatusRequest = {
        status: selectedStatus,
        reason: statusReason || undefined,
      };
      await updateStatus.mutateAsync({ id: affiliate.id, data });
      toast.success(`Status updated to ${selectedStatus}`);
      setIsChangingStatus(false);
      setSelectedStatus(null);
      setStatusReason('');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateCommissionRate = async () => {
    if (!affiliate) return;
    setIsUpdatingCommission(true);
    try {
      const rate = newCommissionRate.trim() === '' ? null : parseFloat(newCommissionRate);
      if (rate !== null && (isNaN(rate) || rate < 0 || rate > 100)) {
        toast.error('Commission rate must be between 0 and 100');
        setIsUpdatingCommission(false);
        return;
      }
      await updateAffiliateCommissionRate(affiliate.id, rate);
      toast.success('Commission rate updated');
      setIsEditingCommission(false);
      window.location.reload();
    } catch (err) {
      toast.error('Failed to update commission rate');
    } finally {
      setIsUpdatingCommission(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'suspended':
        return 'bg-orange-100 text-orange-700';
      case 'inactive':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return '$0.00';
    return `$${amount.toFixed(2)}`;
  };

  if (error) {
    return (
      <div className="space-y-6">
        <AdminPageHeader title="Affiliate Details" backTo="/admin/affiliates" backLabel="Back to Affiliates" />
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading affiliate: {formatApiError(error).message}
        </div>
      </div>
    );
  }

  if (isLoading || !affiliate) {
    return (
      <div className="p-8 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <AdminPageHeader
        title={affiliate.userName || 'Unnamed Affiliate'}
        subtitle={affiliate.userEmail}
        backTo="/admin/affiliates"
        backLabel="Back to Affiliates"
        action={
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setShowEditModal(true)}>
              <i className={`hm hm-${ICONS.EDIT} mr-2`} />
              Edit Affiliate
            </Button>
            <Button
              variant="danger"
              onClick={() => setDeleteDialog(true)}
              disabled={deleteAffiliateMutation.isPending}
            >
              Delete Affiliate
            </Button>
          </div>
        }
      />

      {/* Affiliate Info Card */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Affiliate Information</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">Email</label>
              <p className="mt-1 text-gray-900">{affiliate.userEmail}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Name</label>
              <p className="mt-1 text-gray-900">{affiliate.userName || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Referral Code</label>
              <div className="mt-1 flex items-center gap-2">
                <code className="px-3 py-1.5 bg-gray-100 text-gray-900 rounded-md font-mono text-sm">
                  {affiliate.referralCode}
                </code>
                <Tooltip content="Copy to clipboard">
                  <button
                    onClick={handleCopyReferralCode}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    <i className={`hm hm-${ICONS.COPY} text-lg`} />
                  </button>
                </Tooltip>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <div className="mt-1">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(affiliate.status)}`}>
                  {affiliate.status.toUpperCase()}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Total Clicks</label>
              <p className="mt-1 text-gray-900">{affiliate.totalClicks}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Total Conversions</label>
              <p className="mt-1 text-gray-900">{affiliate.totalConversions}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Total Commissions</label>
              <p className="mt-1 text-gray-900 font-semibold">{formatCurrency(affiliate.totalCommissions)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Pending Payout</label>
              <p className="mt-1 text-gray-900 font-semibold">{formatCurrency(affiliate.pendingPayout)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Min Payout</label>
              <p className="mt-1 text-gray-900">{formatCurrency(affiliate.minPayout)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Created</label>
              <p className="mt-1 text-gray-900">{formatDateTime(affiliate.createdAt)}</p>
            </div>
          </div>

          {/* Commission Rate */}
          <div className="col-span-2 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-600">Custom Commission Rate</label>
                <p className="mt-1 text-gray-900">
                  {affiliate.customCommissionRate !== null && affiliate.customCommissionRate !== undefined
                    ? `${affiliate.customCommissionRate}%`
                    : <span className="text-gray-500 italic">Using default rate</span>
                  }
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={() => {
                setIsEditingCommission(true);
                setNewCommissionRate(affiliate.customCommissionRate?.toString() || '');
              }}>
                <i className={`hm hm-${ICONS.EDIT} mr-2`} />
                Edit
              </Button>
            </div>

            {isEditingCommission && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">
                    Commission Rate (%) - Leave empty to use default
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={newCommissionRate}
                    onChange={(e) => setNewCommissionRate(e.target.value)}
                    placeholder="e.g., 25 for 25%"
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a value between 0 and 100, or leave empty to use the system default rate.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleUpdateCommissionRate}
                    disabled={isUpdatingCommission}
                    loading={isUpdatingCommission}
                  >
                    Save Rate
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsEditingCommission(false);
                      setNewCommissionRate('');
                    }}
                    disabled={isUpdatingCommission}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Status Management */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Status Management</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          {!isChangingStatus ? (
            <Button variant="secondary" onClick={() => setIsChangingStatus(true)}>
              <i className={`hm hm-${ICONS.TOOLS} mr-2`} />
              Change Status
            </Button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">
                  New Status
                </label>
                <Select
                  value={selectedStatus || affiliate.status}
                  onValueChange={(value) => setSelectedStatus(value as typeof STATUS_OPTIONS[number])}
                  options={STATUS_OPTIONS.map(status => ({
                    value: status,
                    label: status.toUpperCase()
                  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Reason for status change"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleChangeStatus}
                  disabled={!selectedStatus || selectedStatus === affiliate.status || updateStatus.isPending}
                  loading={updateStatus.isPending}
                >
                  Save Status
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsChangingStatus(false);
                    setSelectedStatus(null);
                    setStatusReason('');
                  }}
                  disabled={updateStatus.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Conversions (Referrals) */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Conversions ({(affiliate.referrals || []).length})</h2>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Converted
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(affiliate.referrals || []).length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="No conversions yet" />
                  </td>
                </tr>
              ) : (
                (affiliate.referrals || []).map((conversion) => (
                  <tr key={conversion.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm font-mono text-gray-900">{conversion.orderId}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{conversion.productName || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">{formatCurrency(conversion.orderTotal)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-green-600">{formatCurrency(conversion.commissionAmount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(conversion.status)}`}>
                        {conversion.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDateOnly(conversion.convertedAt)}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit Affiliate Modal */}
      {showEditModal && (
        <EditAffiliateModal
          affiliateId={affiliateId}
          currentMinPayout={affiliate.minPayout}
          currentCommissionRate={affiliate.customCommissionRate}
          onClose={() => setShowEditModal(false)}
          updateAffiliate={updateAffiliate}
        />
      )}

      {/* Delete Affiliate Confirmation */}
      <ConfirmDialog
        open={deleteDialog}
        onOpenChange={setDeleteDialog}
        title="Delete Affiliate"
        description={`Delete this affiliate? This is permanent. Affiliates with conversions or payouts cannot be deleted.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deleteAffiliateMutation.isPending}
      />

      {/* Payouts */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Payout History ({(affiliate.payoutHistory || []).length})</h2>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requested
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Processed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(affiliate.payoutHistory || []).length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="No payouts yet" />
                  </td>
                </tr>
              ) : (
                (affiliate.payoutHistory || []).map((payout) => (
                  <tr key={payout.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(payout.amount)} {payout.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payout.paymentMethod || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge
                        variant={
                          payout.status === 'paid'
                            ? 'success'
                            : payout.status === 'approved'
                            ? 'info'
                            : payout.status === 'rejected'
                            ? 'danger'
                            : 'warning'
                        }
                      >
                        {payout.status.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDateOnly(payout.requestedAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payout.processedAt ? formatDateOnly(payout.processedAt) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {payout.adminNotes || '-'}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ============================================================================
// LOCAL MODAL: Edit Affiliate
// ============================================================================

interface EditAffiliateModalProps {
  affiliateId: number;
  currentMinPayout: number;
  currentCommissionRate: number | null;
  onClose: () => void;
  updateAffiliate: ReturnType<typeof useUpdateAffiliate>;
}

const EditAffiliateModal = ({
  affiliateId,
  currentMinPayout,
  currentCommissionRate,
  onClose,
  updateAffiliate,
}: EditAffiliateModalProps) => {
  const [minPayout, setMinPayout] = useState(
    currentMinPayout != null ? String(currentMinPayout) : ''
  );
  const [commissionRate, setCommissionRate] = useState(
    currentCommissionRate != null ? String(currentCommissionRate) : ''
  );
  const [inlineError, setInlineError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);

    const parsedMinPayout = minPayout.trim() ? parseFloat(minPayout) : undefined;
    const parsedRate = commissionRate.trim() !== '' ? parseFloat(commissionRate) : null;

    if (parsedMinPayout !== undefined && (isNaN(parsedMinPayout) || parsedMinPayout < 0)) {
      setInlineError('Min payout must be a positive number.');
      return;
    }

    if (parsedRate !== null && (isNaN(parsedRate) || parsedRate < 0 || parsedRate > 100)) {
      setInlineError('Commission rate must be between 0 and 100.');
      return;
    }

    const body: UpdateAffiliateRequest = {
      minPayout: parsedMinPayout,
      customCommissionRate: parsedRate,
    };

    try {
      await updateAffiliate.mutateAsync({ id: affiliateId, body });
      toast.success('Affiliate updated');
      onClose();
    } catch (err) {
      toast.error(formatApiError(err).message);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Edit Affiliate"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={updateAffiliate.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="edit-affiliate-form" disabled={updateAffiliate.isPending} loading={updateAffiliate.isPending}>
            Save Changes
          </Button>
        </ModalFooter>
      }
    >
      <form id="edit-affiliate-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Min Payout ($)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={minPayout}
            onChange={(e) => setMinPayout(e.target.value)}
            placeholder="50"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Custom Commission Rate (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={commissionRate}
            onChange={(e) => setCommissionRate(e.target.value)}
            placeholder="Leave blank to use default rate"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
          />
          <p className="text-xs text-gray-500 mt-1">Leave blank to use the system default commission rate.</p>
        </div>

        {inlineError && <p className="text-sm text-red-600">{inlineError}</p>}
      </form>
    </Modal>
  );
};

export default AdminAffiliateDetail;
